/** API debug mode: module-level store shared by the axios interceptors
 * (src/api/client.ts) and the debug toolbar UI (src/features/debug/).
 *
 * Mirrors tokenStorage.ts: mutable module state + a listener set, consumed
 * from React via `useSyncExternalStore`. No provider needed.
 *
 * The toggle is backed by `sessionStorage` (per-tab, cleared on close) so
 * debug overhead can't be left on forever. The captured request log lives
 * in memory only — `entries` is replaced immutably on every write so
 * `useSyncExternalStore` snapshots stay referentially stable.
 */

const KEY_ENABLED = 'cp_console_debug';
const MAX_ENTRIES = 50;

/** Backend summary attached by ConsoleDebugMiddleware (backend
 *  demo/middleware/debugmode.py) — `_debug` body key or the compact
 *  `X-CP-Debug-Summary` header (which carries only the scalar fields). */
export interface DebugSummary {
  request_id: string | null;
  total_ms: number;
  db_ms: number;
  query_count: number;
  truncated: boolean;
  queries_by_alias?: Record<string, { count: number; ms: number }>;
  queries?: {
    i: number;
    alias: string;
    ms: number;
    offset_ms: number;
    many: boolean;
    sql: string;
  }[];
  messages?: { offset_ms: number; msg: string; data: Record<string, unknown> }[];
}

export interface DebugRequestEntry {
  id: number;
  /** Client-side capture timestamp (Date.now()). */
  ts: number;
  method: string;
  /** Absolute request URL (for HAR export). */
  url: string;
  path: string;
  query: string;
  /** null = network error (no response). */
  status: number | null;
  statusText: string | null;
  durationMs: number;
  /** X-Request-ID response header. */
  requestId: string | null;
  /** Sensitive values (Authorization, …) are redacted at capture time. */
  requestHeaders: Record<string, string>;
  responseHeaders: Record<string, string>;
  /** Serialized response body, capped at capture time (HAR export). */
  responseBody: string | null;
  /** Pre-cap body size in characters; -1 = unknown/unserializable. */
  responseBodySize: number;
  responseBodyTruncated: boolean;
  responseMimeType: string | null;
  summary: DebugSummary | null;
}

function readEnabled(): boolean {
  try {
    return window.sessionStorage.getItem(KEY_ENABLED) === '1';
  } catch {
    return false;
  }
}

let enabled = readEnabled();
let entries: readonly DebugRequestEntry[] = [];
let seq = 0;

const listeners = new Set<() => void>();

function notify() {
  for (const cb of listeners) cb();
}

export function isDebugEnabled(): boolean {
  return enabled;
}

export function setDebugEnabled(value: boolean): void {
  if (enabled === value) return;
  enabled = value;
  try {
    if (value) {
      window.sessionStorage.setItem(KEY_ENABLED, '1');
    } else {
      window.sessionStorage.removeItem(KEY_ENABLED);
    }
  } catch {
    // Private mode without storage — in-memory toggle still works.
  }
  notify();
}

export function getDebugEntries(): readonly DebugRequestEntry[] {
  return entries;
}

export function clearDebugEntries(): void {
  if (entries.length === 0) return;
  entries = [];
  notify();
}

/** Prepend a captured request (newest first), trimming to MAX_ENTRIES. */
export function recordDebugEntry(entry: Omit<DebugRequestEntry, 'id' | 'ts'>): void {
  seq += 1;
  entries = [{ ...entry, id: seq, ts: Date.now() }, ...entries.slice(0, MAX_ENTRIES - 1)];
  notify();
}

export function subscribeDebug(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
